import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import { ROUTES } from '../constants';
import { getApiPath } from '../util';
import { useAuth, useUpdateAuth } from '../contexts';
import { UserDTO } from '../DTO/UserDTO';

type navbarProps = {
	toggleSideMenu: () => void;
};

const RootBox = styled(Box)({
	flexGrow: 1,
});

const SpacedTypography = styled(Typography)(({ theme }) => ({
	margin: theme.spacing(1.25),
}));

const NavButton = styled(Button)(({ theme }) => ({
	margin: theme.spacing(1.25),
}));

const OutlinedNavButton = styled(Button)({
	borderColor: '#fff',
	color: '#fff',
});

const LogoutButton = styled(Button)({
	borderColor: '#fff',
	color: '#fff',
});

const StyledMenuItem = styled(MenuItem)({
	color: 'inherit',
	textDecoration: 'none',
});


const userString = (user: UserDTO | null) => user?.name || null;

const NavBar = (props: navbarProps) => {
	const { user, authenticated } = useAuth();
	const { login, logout } = useUpdateAuth();
	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
	const navigate = useNavigate();

	const handleMenu: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		setAnchorEl(e.target as Element);
	};

	const handleMenuClick = (pageURL: string) => {
		setAnchorEl(null);
		navigate(pageURL);
	};

	const menuItems = [
		{
			title: 'Home',
			pageURL: '/',
			requiresAdmin: false,
		},
		{
			title: 'Application history',
			pageURL: ROUTES.APPLICATIONS,
			requiresAdmin: false,
		},
		{
			title: 'Applications',
			pageURL: ROUTES.APPLICATIONS_ADMIN,
			requiresAdmin: true,
		},
		{
			title: 'Application rows',
			pageURL: ROUTES.APPLICATION_ROWS,
			requiresAdmin: true,
		},
		{
			title: 'Course mappings',
			pageURL: ROUTES.COURSE_MAPPINGS,
			requiresAdmin: false,
		},
		{
			title: 'Users',
			pageURL: ROUTES.USERS,
			requiresAdmin: true,
		},
		{
			title: 'Courses',
			pageURL: ROUTES.COURSES,
			requiresAdmin: true,
		},
		{
			title: 'Universities',
			pageURL: ROUTES.SCHOOLS,
			requiresAdmin: true,
		},
		{
			title: 'Tracks',
			pageURL: ROUTES.MASTERTRACKS,
			requiresAdmin: true,
		},
	];

	return (
		<RootBox>
			<AppBar position="fixed">
				<Toolbar>
					<IconButton
						edge="start"
						sx={{ marginRight: 2 }}
						color="inherit"
						aria-label="menu"
						onClick={handleMenu}
					>
						<MenuIcon />
					</IconButton>
					{user ? (
						<Menu
							id="menu-navbar"
							anchorEl={anchorEl}
							anchorOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
							keepMounted
							transformOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
							open={!!anchorEl}
							onClose={() => setAnchorEl(null)}
						>
							{menuItems
								.filter(
									({ requiresAdmin }) =>
										(user &&
											requiresAdmin &&
											user?.isAdmin) ||
										!requiresAdmin
								)
								.map(({ title, pageURL }) => (
									<StyledMenuItem
										key={title}
										onClick={() => handleMenuClick(pageURL)}
									>
										{title}
									</StyledMenuItem>
								))}
						</Menu>
					) : null}
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						Exchange portal
					</Typography>
					<SpacedTypography variant="body1">
						{userString(user)}
					</SpacedTypography>
					{authenticated ? (
						<NavButton
							variant="outlined"
							color="secondary"
							onClick={(e) => props.toggleSideMenu()}
						>
							Instructions
						</NavButton>
					) : null}
					{authenticated ? (
						<LogoutButton
							variant="outlined"
							color="inherit"
							href="api/session/logout"
						>
							Logout
						</LogoutButton>
					) : (
						<OutlinedNavButton
							variant="outlined"
							href={getApiPath() + '/session/login'}
						>
							Login
						</OutlinedNavButton>
					)}
				</Toolbar>
			</AppBar>
		</RootBox>
	);
};

export default NavBar;
